<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { Check, LogOut, Users } from 'lucide-svelte';

  import { account, endSession, signedIn } from '$lib/stores/auth';
  import { languages, locale, setLocale } from '$lib/i18n';

  /**
   * Who is signed in, and the way out.
   *
   * # Why it exists
   *
   * There was no way to sign out. Not in the top bar, not in the sidebar, not
   * on the dashboard — `endSession` was called by nothing in the app. A
   * customer on a shared machine could only clear the browser's storage or
   * wait twelve hours for the token to lapse.
   *
   * # It says whose session it is before it offers to end it
   *
   * The email rather than only a name, because the question somebody opens
   * this to answer is usually "am I in the right account" — two seats at one
   * organisation share a company name and differ by exactly that line.
   *
   * # No balance here
   *
   * The organisation carries one, and this is where a menu like this usually
   * puts it. It is deliberately absent: the members' app stopped showing costs
   * at all when the export flow replaced per-record pricing, and a running
   * total in the corner would put them back in the most permanent place on the
   * screen. What an export costs is answered by the export dialog, at the
   * moment it is being decided.
   *
   * # The language belongs here
   *
   * It was a `select` of its own in the bar, permanently occupying width for a
   * choice most customers make once and never revisit. It is a setting about
   * the person rather than about what is on screen, which is what this menu
   * collects — and the drawer keeps its own copy, because on mobile there is no
   * menu to open.
   */

  let open = $state(false);
  let container = $state<HTMLElement | null>(null);
  let trigger = $state<HTMLButtonElement | null>(null);

  const person = $derived($account);

  /** The display name, falling back to the part of the address before the @. */
  const name = $derived(person?.full_name?.trim() || person?.email.split('@')[0] || '');

  /**
   * Up to two letters for the avatar.
   *
   * From the name's words rather than its first two characters, so "Michael
   * Kaune" is MK and not MI. A single-word name gives one letter, which reads
   * better than an initial paired with whatever happens to follow it.
   */
  const initials = $derived(
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('')
  );

  function close() {
    open = false;
    // Focus returns to where it came from, or a keyboard user dismissing this
    // is dropped at the top of the document.
    trigger?.focus();
  }

  function onWindowClick(event: MouseEvent) {
    if (!open || !container) return;
    if (!container.contains(event.target as Node)) open = false;
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      close();
    }
  }

  /**
   * Sign out.
   *
   * `endSession` and nothing else. In the members' build the shell watches for
   * a screen with no session and sends it to the login page itself, so routing
   * from here would be the same decision made twice; in the public build there
   * is nowhere to go — the rows simply mask again, which is what signing out
   * means there.
   */
  function signOut() {
    open = false;
    endSession();
  }
</script>

<svelte:window on:click={onWindowClick} on:keydown={onKeydown} />

{#if $signedIn && person}
  <div class="relative shrink-0" bind:this={container}>
    <!--
      Open is a brighter cut of the same translucent chip rather than the
      accent, and it stays that way now the accent is purple again. The accent
      is #7038b0 and the bar is #1c2647 — 2.04:1, a dark blob on a dark bar.
      Two dark colours do not signal state however well they are chosen; more
      white does.
    -->
    <button
      bind:this={trigger}
      type="button"
      onclick={() => (open = !open)}
      aria-expanded={open}
      aria-haspopup="menu"
      title={person.email}
      class="flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold transition-colors {open
        ? 'border-white/40 bg-white/25 text-white'
        : 'chrome-control'}"
    >
      <span class="sr-only">{$_('account.menu')}</span>
      {initials}
    </button>

    {#if open}
      <div
        role="menu"
        tabindex="-1"
        class="absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-surface border border-hairline bg-white shadow-overlay"
      >
        <div class="border-b border-hairline px-4 py-3">
          <p class="truncate text-sm font-medium text-gray-900">{name}</p>
          <p class="truncate text-xs text-gray-500">{person.email}</p>

          {#if person.organisation}
            <!-- Which organisation's seat this is. A shared list, an export and
                 an invoice all belong to it rather than to the person, so it is
                 the other half of "am I in the right account". -->
            <p class="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
              <Users class="h-3.5 w-3.5 shrink-0 text-gray-400" />
              <span class="truncate">{person.organisation.name}</span>
            </p>
          {/if}
        </div>

        <!--
          The language, by its own name rather than a two-letter code: there is
          room for "Deutsch" here, and a menu of languages that says DE is a
          menu asking the reader to expand an abbreviation.
        -->
        <div class="border-b border-hairline py-1">
          <p class="px-4 py-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {$_('account.language')}
          </p>
          {#each languages as language (language.code)}
            <button
              type="button"
              role="menuitemradio"
              aria-checked={$locale === language.code}
              onclick={() => setLocale(language.code)}
              class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-surface-canvas {$locale ===
              language.code
                ? 'font-medium text-gray-900'
                : 'text-gray-700'}"
            >
              <!-- The tick keeps its space when it is not drawn, so choosing a
                   language does not shift the row under the pointer. -->
              <span class="flex h-4 w-4 shrink-0 items-center justify-center">
                {#if $locale === language.code}
                  <Check class="h-4 w-4 text-accent-600" />
                {/if}
              </span>
              {language.name}
            </button>
          {/each}
        </div>

        <button
          type="button"
          role="menuitem"
          onclick={signOut}
          class="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 transition-colors hover:bg-surface-canvas"
        >
          <LogOut class="h-4 w-4 shrink-0 text-gray-400" />
          {$_('account.signOut')}
        </button>
      </div>
    {/if}
  </div>
{/if}
